import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Spin, Typography, message } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { twoFactorSetup, twoFactorEnable } from '../modules/auth/api';

/**
 * OVO-159 — TOTP enrollment panel: QR + manual key → first code → recovery
 * codes. Used by BOTH the voluntary "Two-factor authentication" modal in the
 * header and the forced-enrollment step on the login page (where `token` is
 * the setup-scoped JWT returned by /auth/login under enforcement).
 *
 * onDone(enableResponse) fires after the user confirms they saved the
 * recovery codes; the response carries the fresh `access_token` (twoFactor
 * claim) so the caller can install the session.
 */
const TwoFactorEnroll = ({ token, onDone }) => {
  const { t } = useTranslation('auth');
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [enabled, setEnabled] = useState(null); // enable response (recovery codes stage)

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { response, error } = await twoFactorSetup(token);
      if (!mounted) return;
      setLoading(false);
      if (error) {
        message.error(error?.message || 'Setup failed');
        return;
      }
      setSetup(response);
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleEnable = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    const { response, error } = await twoFactorEnable(code.trim(), token);
    setSubmitting(false);
    if (error) {
      message.error(error?.message || t('twoFactorInvalidCode', 'Invalid code — try again.'));
      return;
    }
    setEnabled(response);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (enabled) {
    return (
      <div className="two-factor-enroll">
        <Alert
          type="warning"
          showIcon
          message={t('twoFactorRecoveryTitle', 'Save your recovery codes')}
          description={t(
            'twoFactorRecoveryHint',
            'Each code works once if you lose access to your authenticator app. Store them somewhere safe — they are shown only now.',
          )}
          style={{ marginBottom: 12 }}
        />
        <pre
          style={{
            background: 'var(--surface-2)',
            padding: 12,
            borderRadius: 6,
            fontSize: 14,
            lineHeight: 1.7,
            userSelect: 'all',
          }}
        >
          {(enabled.recovery_codes || []).join('\n')}
        </pre>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => {
              navigator.clipboard?.writeText((enabled.recovery_codes || []).join('\n'));
              message.success(t('copied', 'Copied'));
            }}
          >
            {t('twoFactorCopyCodes', 'Copy codes')}
          </Button>
          <Button type="primary" onClick={() => onDone && onDone(enabled)}>
            {t('twoFactorSavedContinue', 'I saved them — continue')}
          </Button>
        </div>
      </div>
    );
  }

  if (!setup) return null;

  return (
    <div className="two-factor-enroll">
      <Typography.Paragraph>
        {t(
          'twoFactorScanQr',
          'Scan this QR code with your authenticator app (Google Authenticator, 1Password, Aegis…), then enter the 6-digit code it shows.',
        )}
      </Typography.Paragraph>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <img src={setup.qr_data_url} alt="TOTP QR" style={{ width: 200, height: 200 }} />
      </div>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
        {t('twoFactorManualKey', 'Manual key')}:{' '}
        <Typography.Text code copyable>
          {setup.secret}
        </Typography.Text>
      </Typography.Paragraph>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          size="large"
          prefix={<SafetyOutlined />}
          placeholder="123 456"
          value={code}
          maxLength={8}
          onChange={(e) => setCode(e.target.value)}
          onPressEnter={handleEnable}
          autoFocus
        />
        <Button type="primary" size="large" loading={submitting} onClick={handleEnable}>
          {t('twoFactorActivate', 'Activate')}
        </Button>
      </div>
    </div>
  );
};

export default TwoFactorEnroll;
